import type { ReactElement, FormEvent, KeyboardEvent } from 'react';
import type { StoryVersion } from '@/types/story';
import type { User } from '@/types/user';
import type { LucideIcon } from 'lucide-react';

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageLayout from '@/components/PageLayout';
import VersionBadge from '@/components/VersionBadge';
import PublicSeriesCard from '@/components/PublicSeriesCard';
import ClickableAuthor from '@/components/ClickableAuthor';
import {
  Search, Sparkles, Heart, Eye, User as UserIcon, BookOpen, ChevronRight, Star, Library, Loader2, Film
} from 'lucide-react';
import { toast } from 'sonner';
import { STORY_CATEGORIES } from '@/constants/categories';
import { getBookTypeLabel } from '@/lib/utils';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

interface SearchStory {
  id: string;
  title: string;
  thumbnail?: string;
  age_range: string;
  version?: StoryVersion;
  is_pro_story?: boolean;
  is_public?: boolean;
  is_favorited?: boolean;
  author_name?: string;
  author_picture?: string;
  user_id: string;
  pages?: Array<{ page_number: number }>;
  favorites_count?: number;
  views_count?: number;
  series_title?: string;
  series_is_public?: boolean;
  has_animations?: boolean;
}

interface SearchSeries {
  id: string;
  title: string;
  description?: string;
  thumbnail?: string;
  age_range: string;
  book_count: number;
  user_name: string;
  user_picture?: string;
}

interface SearchResponse {
  stories: SearchStory[];
  total: number;
  hasMore: boolean;
}

interface SeriesSearchResponse {
  series: SearchSeries[];
}

interface FeaturedAuthor {
  user_id: string;
  name: string;
  picture?: string;
}

interface CategoryWithIcon {
  id: string;
  label: string;
  icon: LucideIcon;
}

interface StoryListItemProps {
  story: SearchStory;
}

// Helper to get full image URL (handles both local /static/ paths and external URLs)
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Build categories with icons from shared constants
const CATEGORIES_WITH_ICONS: CategoryWithIcon[] = STORY_CATEGORIES.map(cat => ({
  id: cat.value,
  label: cat.label,
  icon: cat.icon || Sparkles
}));

const SearchPage = (): ReactElement => {
  const navigate = useNavigate();
  const [query, setQuery] = useState<string>('');
  const [stories, setStories] = useState<SearchStory[]>([]);
  const [seriesResults, setSeriesResults] = useState<SearchSeries[]>([]);
  const [featuredStories, setFeaturedStories] = useState<SearchStory[]>([]);
  const [featuredAuthors, setFeaturedAuthors] = useState<FeaturedAuthor[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searched, setSearched] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(() => {
    try {
      const cached = localStorage.getItem("cached_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const ITEMS_PER_PAGE = 25;

  useEffect(() => {
    checkAuth();
    loadFeaturedContent();
  }, []);

  // Search when categories change
  useEffect(() => {
    if (selectedCategories.length > 0) {
      searchByCategories(selectedCategories, true);
    } else if (searched && !query.trim()) {
      // If no categories selected and no query, go back to featured view
      setSearched(false);
    }
  }, [selectedCategories]);

  // Infinite scroll handler
  const handleLoadMore = useCallback(() => {
    if (query.trim()) {
      handleSearch(undefined, false);
    } else if (selectedCategories.length > 0) {
      searchByCategories(selectedCategories, false);
    }
  }, [query, selectedCategories, stories.length, hasMore]);

  // Hook up infinite scroll - returns a ref to attach to loader element
  const loaderRef = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    hasMore: searched ? hasMore : false,
    loading: loading || loadingMore
  });

  const checkAuth = async (): Promise<void> => {
    try {
      const response = await axios.get<User>(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    }
  };

  const loadFeaturedContent = async (): Promise<void> => {
    try {
      // Load featured/trending stories
      const storiesRes = await axios.get<SearchStory[]>(`${API}/stories/explore?limit=9&sort=trending`);
      setFeaturedStories(storiesRes.data);

      // Extract unique authors from stories
      const authorsMap = new Map<string, FeaturedAuthor>();
      storiesRes.data.forEach((story) => {
        if (story.author_name && !authorsMap.has(story.user_id)) {
          authorsMap.set(story.user_id, {
            user_id: story.user_id,
            name: story.author_name,
            picture: story.author_picture
          });
        }
      });
      setFeaturedAuthors(Array.from(authorsMap.values()).slice(0, 6));
    } catch (error) {
      console.error('Failed to load featured content:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSearch = async (e?: FormEvent<HTMLFormElement>, reset: boolean = true): Promise<void> => {
    e?.preventDefault();
    if (!query.trim()) {
      setSearched(false);
      return;
    }

    if (!reset && (!hasMore || loadingMore)) return;

    if (reset) {
      setLoading(true);
      setStories([]);
    } else {
      setLoadingMore(true);
    }

    setSearched(true);

    try {
      const offset = reset ? 0 : stories.length;
      const params = new URLSearchParams();
      params.append('q', query);
      params.append('limit', ITEMS_PER_PAGE.toString());
      params.append('offset', offset.toString());

      // Search both stories and series (only series on first load)
      const promises: Promise<any>[] = [
        axios.get<SearchResponse>(`${API}/stories/search?${params}`, {
          withCredentials: true
        })
      ];

      // Only fetch series on first load (reset)
      if (reset) {
        promises.push(
          axios.get<SeriesSearchResponse>(`${API}/v1/series/public/search?q=${encodeURIComponent(query)}`, {
            withCredentials: true
          })
        );
      }

      const results = await Promise.all(promises);
      const storiesRes = results[0];

      if (reset) {
        setStories(storiesRes.data.stories);
        setSeriesResults(results[1]?.data.series || []);
      } else {
        setStories(prev => [...prev, ...storiesRes.data.stories]);
      }

      setTotal(storiesRes.data.total);
      setHasMore(storiesRes.data.hasMore);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Search on Enter key
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle category click - toggle selection
  const handleCategoryClick = (category: CategoryWithIcon): void => {
    setSelectedCategories(prev => {
      if (prev.includes(category.id)) {
        // Deselect
        return prev.filter(id => id !== category.id);
      } else {
        // Select
        return [...prev, category.id];
      }
    });
  };

  // Search by multiple categories
  const searchByCategories = async (categoryIds: string[], reset: boolean = true): Promise<void> => {
    if (!reset && (!hasMore || loadingMore)) return;

    if (reset) {
      setLoading(true);
      setStories([]);
    } else {
      setLoadingMore(true);
    }

    setSearched(true);

    try {
      const offset = reset ? 0 : stories.length;
      const params = new URLSearchParams();
      params.append('categories', categoryIds.join(','));
      params.append('limit', ITEMS_PER_PAGE.toString());
      params.append('offset', offset.toString());

      // Search by categories only - backend handles OR logic
      const response = await axios.get<SearchResponse>(`${API}/stories/search?${params}`, {
        withCredentials: true
      });

      if (reset) {
        setStories(response.data.stories);
      } else {
        setStories(prev => [...prev, ...response.data.stories]);
      }

      setTotal(response.data.total);
      setHasMore(response.data.hasMore);
    } catch (error) {
      console.error('Category search failed:', error);
      toast.error('Failed to load stories for selected categories.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Clear all category selections
  const clearCategories = (): void => {
    setSelectedCategories([]);
    setSearched(false);
  };

  // Story list item component (matching Library page style)
  const StoryListItem = ({ story }: StoryListItemProps): ReactElement => (
    <div
      className="flex items-center gap-4 p-3 hover:bg-black/5 dark:hover:bg-white/10 hover:scale-[1.01] transition-all duration-200 cursor-pointer group"
      onClick={() => navigate(`/story/${story.id}`)}
      style={{ borderBottom: '1px solid var(--st-border-light)' }}
    >
      {/* Thumbnail with page count overlay */}
      <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden flex-shrink-0">
        {story.thumbnail ? (
          <img src={getImageUrl(story.thumbnail) || undefined} alt={story.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}>
            <Sparkles className="w-6 h-6" style={{ color: 'var(--st-primary-hover)' }} />
          </div>
        )}
        {story.pages && story.pages.length > 0 && (
          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-xs font-bold text-white" style={{ background: 'rgba(0,0,0,0.6)', fontSize: '10px' }}>
            {story.pages?.length || 0}p
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="font-bold text-sm md:text-base truncate" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
            {story.title}
          </h3>
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 bg-gray-200 dark:bg-gray-700" style={{ color: 'var(--st-text-heading)' }}>
            {getBookTypeLabel(story.age_range)}
          </span>
          <VersionBadge version={story.version} isProStory={story.is_pro_story} size="small" />
          {story.series_title && story.series_is_public && (
            <span title={`Part of: ${story.series_title}`}>
              <Library className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#4F46E5' }} />
            </span>
          )}
          {story.has_animations && (
            <span title="Animated Pages">
              <Film className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#F59E0B' }} />
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <ClickableAuthor
            userId={story.user_id}
            name={story.author_name}
            picture={story.author_picture}
            size="small"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="hidden md:flex items-center gap-4 text-xs flex-shrink-0" style={{ color: 'var(--st-text-muted)' }}>
        {story.views_count && story.views_count > 0 && (
          <span className="flex items-center gap-1">
            <Eye className="w-4 h-4" />
            {story.views_count}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Heart className="w-4 h-4" fill={story.is_favorited ? 'var(--st-primary)' : 'none'} stroke={story.is_favorited ? 'var(--st-primary)' : 'currentColor'} />
          {story.favorites_count || 0}
        </span>
      </div>
    </div>
  );

  return (
    <PageLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-screen-2xl mx-auto px-4 py-4 md:py-6 md:px-8 2xl:px-12">
          {/* Header */}
          <div className="rounded-2xl p-4 md:p-5 mb-5 shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl md:text-2xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  Search Stories
                </h1>
                <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  Discover magical stories from our community
                </p>
              </div>
              <button
                onClick={() => navigate('/create')}
                className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm"
                style={{
                  background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                  color: 'white',
                  fontFamily: 'Quicksand, sans-serif'
                }}
              >
                <Sparkles className="w-4 h-4" />
                Create Story
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-5">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
              <input
                type="text"
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search stories..."
                className="w-full pl-11 pr-4 py-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 dark:focus:ring-orange-800 transition-all shadow-sm"
                style={{
                  fontFamily: 'Quicksand, sans-serif',
                  border: '1px solid var(--st-border-light)',
                  background: 'var(--st-bg-surface)',
                  color: 'var(--st-text-heading)'
                }}
              />
            </div>
          </div>

          {/* Selected Categories Pills (shown when categories are selected) */}
          {selectedCategories.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  Filtering by:
                </span>
                {selectedCategories.map(catId => {
                  const cat = CATEGORIES_WITH_ICONS.find(c => c.id === catId);
                  const CatIcon = cat?.icon;
                  return (
                    <button
                      key={catId}
                      onClick={() => cat && handleCategoryClick(cat)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all hover:opacity-80"
                      style={{
                        background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                        color: 'white',
                        fontFamily: 'Quicksand, sans-serif'
                      }}
                    >
                      {CatIcon && <CatIcon className="w-3.5 h-3.5" />}
                      {cat?.label}
                      <span className="ml-1 text-white/80 hover:text-white">x</span>
                    </button>
                  );
                })}
                <button
                  onClick={clearCategories}
                  className="text-sm underline hover:no-underline"
                  style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-primary)' }}
                >
                  Clear all
                </button>
              </div>
            </div>
          )}

          {/* Categories Section - Always visible */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                Categories
              </h2>
              {selectedCategories.length > 0 && (
                <button
                  onClick={clearCategories}
                  className="text-sm font-semibold px-3 py-1 rounded-full transition-all hover:bg-orange-100 dark:hover:bg-orange-900/30"
                  style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-primary)' }}
                >
                  Clear all ({selectedCategories.length})
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-[#F5F0EB] dark:bg-white/10">
              {CATEGORIES_WITH_ICONS.map((category) => {
                const isSelected = selectedCategories.includes(category.id);
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryClick(category)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                      isSelected ? 'shadow-sm' : 'hover:bg-white/50 dark:hover:bg-white/10'
                    }`}
                    style={{
                      fontFamily: 'Quicksand, sans-serif',
                      color: isSelected ? 'var(--st-text-heading)' : 'var(--st-text-muted)',
                      ...(isSelected ? { background: 'var(--st-bg-surface)' } : {})
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: isSelected ? 'var(--st-primary)' : 'var(--st-text-muted)' }} />
                    {category.label}
                    {isSelected && <span className="ml-1" style={{ color: 'var(--st-primary)' }}>✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search Results */}
          {searched ? (
            loading ? (
              <div className="text-center py-16 rounded-2xl shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                <BookOpen className="w-12 h-12 mx-auto mb-4 animate-pulse" style={{ color: 'var(--st-primary)' }} />
                <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Searching...</p>
              </div>
            ) : stories.length === 0 && seriesResults.length === 0 ? (
              <div className="text-center py-16 rounded-2xl shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                <Search className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--st-border-medium)' }} />
                <p className="text-lg font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  {selectedCategories.length > 0
                    ? `No stories found in selected categories`
                    : `No results for "${query}"`}
                </p>
                <p className="text-sm mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  {selectedCategories.length > 0
                    ? 'Try selecting different categories or create a story in this category!'
                    : 'Try a different search term'}
                </p>
                {selectedCategories.length > 0 && (
                  <button
                    onClick={clearCategories}
                    className="px-5 py-2.5 rounded-lg font-semibold text-sm transition-all hover:scale-105"
                    style={{
                      background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                      color: 'white',
                      fontFamily: 'Quicksand, sans-serif'
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {/* Series Results */}
                {seriesResults.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Library className="w-5 h-5" style={{ color: '#4F46E5' }} />
                        <h2 className="text-base font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                          Series
                        </h2>
                      </div>
                      <span className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                        {seriesResults.length} {seriesResults.length === 1 ? 'series' : 'series'} found
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {seriesResults.slice(0, 4).map((s) => (
                        <PublicSeriesCard key={s.id} series={s} onClick={undefined} onFavoriteChange={undefined} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Story Results */}
                {stories.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
                        <h2 className="text-base font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                          {selectedCategories.length > 0 ? 'Stories' : 'Stories'}
                        </h2>
                      </div>
                      <span className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                        {stories.length} {stories.length === 1 ? 'story' : 'stories'} found
                      </span>
                    </div>

                    {/* Results list */}
                    <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                      {stories.map((story) => (
                        <StoryListItem key={story.id} story={story} />
                      ))}
                    </div>

                    {/* Infinite scroll trigger element */}
                    {hasMore && (
                      <div ref={loaderRef} className="flex justify-center py-6 mt-4">
                        {loadingMore && (
                          <>
                            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--st-primary)' }} />
                            <p className="text-sm ml-2" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                              Loading more stories...
                            </p>
                          </>
                        )}
                      </div>
                    )}

                    {/* End of Results */}
                    {!hasMore && stories.length > 0 && (
                      <div className="text-center py-6 mt-4">
                        <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                          You've reached the end!
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          ) : initialLoading ? (
            <div className="text-center py-16 rounded-2xl shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
              <BookOpen className="w-12 h-12 mx-auto mb-4 animate-pulse" style={{ color: 'var(--st-primary)' }} />
              <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Loading...</p>
            </div>
          ) : (
            <>
              {/* Featured Stories Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                    Featured Stories
                  </h2>
                  <button
                    onClick={() => navigate('/explore')}
                    className="flex items-center gap-1 text-sm font-semibold hover:underline"
                    style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-primary)' }}
                  >
                    See all <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Story list */}
                <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                  {featuredStories.map((story) => (
                    <StoryListItem key={story.id} story={story} />
                  ))}
                </div>
              </div>

              {/* Featured Authors Section - Hidden on mobile */}
              {featuredAuthors.length > 0 && (
                <div className="hidden md:block">
                  <h2 className="text-base font-bold mb-3" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                    Featured Authors
                  </h2>

                  <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                    {featuredAuthors.map((author) => (
                      <div
                        key={author.user_id}
                        className="flex flex-col items-center cursor-pointer group flex-shrink-0"
                        onClick={() => navigate(`/user/${author.user_id}`)}
                      >
                        {/* Circular avatar */}
                        <div className="w-16 h-16 rounded-full overflow-hidden mb-2 ring-2 ring-transparent group-hover:ring-orange-200 dark:group-hover:ring-orange-800 transition-all">
                          {author.picture ? (
                            <img src={getImageUrl(author.picture) || undefined} alt={author.name} className="w-full h-full object-cover" />
                          ) : (
                            <div
                              className="w-full h-full flex items-center justify-center"
                              style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}
                            >
                              <UserIcon className="w-6 h-6" style={{ color: 'var(--st-primary)' }} />
                            </div>
                          )}
                        </div>
                        <span
                          className="text-xs font-medium text-center max-w-16 truncate group-hover:underline"
                          style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
                        >
                          {author.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default SearchPage;
